import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import type { SurveyTemplateWithQuestions } from '@/hooks/useSurveyTemplates';

interface SurveyTemplateSEOProps {
  template: SurveyTemplateWithQuestions;
}

export const SurveyTemplateSEO: React.FC<SurveyTemplateSEOProps> = ({ template }) => {
  const { lang } = useParams<{ lang?: string }>();
  const currentLang = lang || 'en';
  const prefix = `/${currentLang}`;
  
  const title = `${template.name} - Survey Template | JiGSO EX Hub`;
  const description = template.description 
    ? `${template.description.slice(0, 140)}... Get this ready-to-use survey template for ${template.category}.`
    : `Professional survey template for ${template.category}. Ready-to-use questions and proven methodology.`;
  
  const url = `${window.location.origin}${prefix}/survey-templates/${template.id}`;
  const questionsCount = template.questions?.length || 0;
  const duration = template.estimated_duration_minutes;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={url} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="JiGSO EX Hub" />
      <meta property="article:section" content={template.category} />
      <meta property="article:tag" content="survey template" />
      <meta property="article:tag" content="employee survey" />
      <meta property="article:tag" content="HR tools" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      
      {/* Language and alternate links */}
      <meta httpEquiv="content-language" content={currentLang} />
      <link rel="alternate" hrefLang="en" href={`${window.location.origin}/en/survey-templates/${template.id}`} />
      <link rel="alternate" hrefLang="fr" href={`${window.location.origin}/fr/survey-templates/${template.id}`} />
      <link rel="alternate" hrefLang="x-default" href={`${window.location.origin}/en/survey-templates/${template.id}`} />
      
      {/* Structured Data for Survey Template */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CreativeWork",
          "name": template.name,
          "description": template.description,
          "url": url,
          "category": template.category,
          "audience": template.target_audience,
          "timeRequired": duration ? `PT${duration}M` : undefined,
          "numberOfItems": questionsCount,
          "inLanguage": currentLang,
          "isAccessibleForFree": true,
          "publisher": {
            "@type": "Organization",
            "name": "JiGSO EX Hub",
            "url": window.location.origin
          },
          "mainEntity": {
            "@type": "Course",
            "name": template.name,
            "description": template.description,
            "provider": {
              "@type": "Organization",
              "name": "JiGSO EX Hub"
            },
            "educationalLevel": "Professional",
            "about": template.category,
            "timeRequired": duration ? `PT${duration}M` : undefined
          }
        })}
      </script>
      
      {/* Breadcrumb Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": `${window.location.origin}${prefix}`
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Survey Templates",
              "item": `${window.location.origin}${prefix}/survey-templates`
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": template.name,
              "item": url
            }
          ]
        })}
      </script>
    </Helmet>
  );
};